
import React from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useFormSyncToHash } from '@/hooks/useFormSyncToHash';

const formSchema = z.object({
  website: z.string().url({
    message: "Please enter a valid URL starting with http:// or https://",
  }).optional().or(z.literal('')),
  facebook_url: z.string().url({
    message: "Please enter a valid URL starting with http:// or https://",
  }).optional().or(z.literal('')),
  instagram_url: z.string().url({
    message: "Please enter a valid URL starting with http:// or https://",
  }).optional().or(z.literal('')),
  tiktok_url: z.string().url({
    message: "Please enter a valid URL starting with http:// or https://",
  }).optional().or(z.literal('')),
  google_maps_url: z.string().url({
    message: "Please enter a valid Google Maps URL",
  }).optional().or(z.literal('')),
  application_comments: z.string().optional()
});

type FormValues = z.infer<typeof formSchema>;

interface BusinessVerificationFormProps {
  formData: any;
  updateFormData: (data: Partial<any>) => void;
  onNext: () => void;
  onPrev: () => void;
}

const BusinessVerificationForm = ({
  formData,
  updateFormData,
  onNext,
  onPrev
}: BusinessVerificationFormProps) => {
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      website: formData.website || '',
      facebook_url: formData.facebook_url || '',
      instagram_url: formData.instagram_url || '',
      tiktok_url: formData.tiktok_url || '',
      google_maps_url: formData.google_maps_url || '',
      application_comments: formData.application_comments || ''
    },
  });

  // Use the custom hook to sync form data to URL hash
  useFormSyncToHash(form.watch, formData, updateFormData);

  const onSubmit = (values: FormValues) => {
    updateFormData(values);
    onNext();
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold mb-2">Business Verification</h2>
        <p className="text-muted-foreground">
          Help us verify your business by providing your online presence and any additional information that supports your application.
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="space-y-6">
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="website"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Website URL</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                          https://
                        </span>
                        <Input 
                          type="text"
                          placeholder="www.yourbusiness.com" 
                          className="pl-16"
                          {...field}
                          value={field.value?.replace(/^https?:\/\//, '') || ''}
                          onChange={(e) => {
                            const value = e.target.value.trim();
                            // Add https:// prefix if not already present
                            if (value) {
                              field.onChange(`https://${value}`);
                            } else {
                              field.onChange('');
                            }
                          }}
                        />
                      </div>
                    </FormControl>
                    <FormDescription className="text-xs">
                      Enter your website without the https:// prefix
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="facebook_url"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Facebook</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                            https://
                          </span>
                          <Input 
                            type="text"
                            placeholder="facebook.com/yourbusiness" 
                            className="pl-16"
                            {...field}
                            value={field.value?.replace(/^https?:\/\//, '') || ''}
                            onChange={(e) => {
                              const value = e.target.value.trim();
                              if (value) {
                                field.onChange(`https://${value}`);
                              } else {
                                field.onChange('');
                              }
                            }}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="instagram_url"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Instagram</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                            https://
                          </span>
                          <Input 
                            type="text"
                            placeholder="instagram.com/yourbusiness" 
                            className="pl-16"
                            {...field}
                            value={field.value?.replace(/^https?:\/\//, '') || ''}
                            onChange={(e) => {
                              const value = e.target.value.trim();
                              if (value) {
                                field.onChange(`https://${value}`);
                              } else {
                                field.onChange('');
                              }
                            }}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="tiktok_url"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>TikTok</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                            https://
                          </span>
                          <Input 
                            type="text"
                            placeholder="tiktok.com/@yourbusiness" 
                            className="pl-16"
                            {...field}
                            value={field.value?.replace(/^https?:\/\//, '') || ''}
                            onChange={(e) => {
                              const value = e.target.value.trim();
                              if (value) {
                                field.onChange(`https://${value}`);
                              } else {
                                field.onChange('');
                              }
                            }}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="google_maps_url"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Google Maps Link</FormLabel>
                      <FormControl>
                        <Input 
                          type="url"
                          pattern="^https?:\/\/.+"
                          title="Please enter a valid URL starting with http:// or https://"
                          placeholder="https://maps.google.com/..." 
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
            
            <FormField
              control={form.control}
              name="application_comments"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Additional Comments</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Share any additional information that would help us understand your business and approve your application..."
                      className="min-h-[100px]"
                      {...field} 
                    />
                  </FormControl>
                  <FormDescription>
                    Tell us anything else about your business that would help with the review process.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          
          <div className="flex justify-between pt-6">
            <Button type="button" variant="outline" onClick={onPrev}>Previous</Button>
            <Button type="submit">Next</Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default BusinessVerificationForm;
